import React, { useState, useContext, useEffect, useRef } from 'react';
import './MenuButtons.css';
import { ModalContext } from '../../../context/ModalContext.js';
import { Chip } from '../../Chip/chip.js';
import ExportCsv from '../../TableView/ExportCsv.js';
import policies from '../../../geojson/policies_all_ctus_counties.json';

function MenuButtons({ map, onLayerChange, activeLayer, onFilterChange, toggleFilterMenu, toggleZoomTo, isFilterActive, filters, closeFilterMenu, closeZoomToMenu }) {
    const [activeDropdown, setActiveDropdown] = useState(null);

    const { openModal } = useContext(ModalContext);
    const [isMapReady, setIsMapReady] = useState(false);
    const mapRef = useRef(null);
    const [visibleLayers, setVisibleLayers] = useState([]);
    const [tableData, setTableData] = useState([]);

    // Transform GeoJSON to table data
    const transformGeoJSONToTableData = (filters) => {
        const data = [];
        policies.features.forEach(feature => {
            const properties = feature.properties;
            const populationFilter = filters.communitySize[0]?.split('_')[0];

            if ((filters.policyTypes.length == 0 || filters.policyTypes.includes(properties.category)) &&
                (filters.policySubtypes.length == 0 || filters.policySubtypes.includes(properties.subcat)) &&
                (filters.communitySize.length == 0 || Number.isNaN(Number.parseFloat(populationFilter)) || (properties.pop20 > populationFilter))) {
                properties.join_name = properties.name || 'N/A';
                properties.policy_nam = `${properties.policy_ct || 0} policies`;
                properties.pop20 = properties.pop20 || 0;
                properties.link = properties.mc_link || null;
                data.push(properties);
            }
        });
        return data;
    };

    // Update table data when filters change
    useEffect(() => {
        const data = transformGeoJSONToTableData(filters);
        setTableData(data);
    }, [filters]);

    // Store the map reference in a ref to avoid dependency issues
    useEffect(() => {
        if (map) {
            console.log("MenuButtons received map instance");
            mapRef.current = map;
            
            // Check if the map is fully loaded
            if (map.loaded()) {
                console.log("Map already loaded when received by MenuButtons");
                setIsMapReady(true);
            } else {
                console.log("Setting up load event listener in MenuButtons");
                const onLoad = () => {
                    console.log("Map load event detected in MenuButtons");
                    setIsMapReady(true);
                };
                map.on('load', onLoad);
                
                // Clean up listener if component unmounts
                return () => {
                    if (mapRef.current) {
                        mapRef.current.off('load', onLoad);
                    }
                };
            }
        } else {
            console.log("MenuButtons: No map instance received");
        }
    }, [map]);

    // toggleLayer function
    const toggleLayer = (layerId) => {
        console.log(`Attempting to toggle layer: ${layerId}`);
        
        const currentMap = mapRef.current;
        
        // if map exists
        if (!currentMap) {
            console.error('Map is not available');
            return;
        }
        
        // if map is loaded
        if (!currentMap.loaded()) {
            console.error('Map is not fully loaded yet, cannot toggle layer');
            return;
        }
        
        try {
            // Get current visibility
            const visibility = currentMap.getLayoutProperty(layerId, 'visibility');
            console.log(`Current visibility of ${layerId}: ${visibility}`);
            
            // Toggle visibility
            const newVisibility = visibility === 'visible' ? 'none' : 'visible';
            console.log(`Setting ${layerId} visibility to: ${newVisibility}`);
            
            currentMap.setLayoutProperty(layerId, 'visibility', newVisibility);
            
            // Update visible layers tracking
            if (newVisibility === 'visible') {
                setVisibleLayers(prev => [...prev, layerId]);
                onLayerChange(layerId);
            } else {
                setVisibleLayers(prev => prev.filter(id => id !== layerId));
                if (activeLayer === layerId) {
                    onLayerChange(null);
                }
            }
            
            console.log(`Layer ${layerId} visibility toggled successfully`);
        } catch (error) {
            console.error(`Error toggling layer ${layerId}:`, error);
        }
    };

    const [layersFilters, setLayersFilters] = useState([
        {value: 'layer1', label: 'Layer 1', active: true },
        {value: 'layer2', label: 'Layer 2', active: false },
        //add more layers when needed
    ]);

    const toggleDropdownHide = (showDropdown, ...hideDropdowns) => {
        setActiveDropdown(activeDropdown === showDropdown ? null : showDropdown);
        // Close external menus when opening internal dropdowns
        if (activeDropdown !== showDropdown && showDropdown !== null) {
            if (closeFilterMenu) closeFilterMenu();
            if (closeZoomToMenu) closeZoomToMenu();
        }
    };

    const toggleLayersMenu = () => {
        console.log('Toggle Layers menu clicked');
        closeFilterMenu(); // Close the filter menu
        closeZoomToMenu(); // Close the zoom to menu
        toggleDropdownHide('layers');
    };

    const showAbout = () => {
        openModal('popup'); // Open the popup modal
    };

    const showHow = () => {
        openModal('about'); // Open the about modal
    };


    const showTableView = () => {
        openModal('tableView'); // Open the table modal
    };

    const handleChipClick = (layerValue) => {
        setLayersFilters((prevLayers) => 
        prevLayers.map((layer) =>
            layer.value === layerValue ? { ...layer, active: !layer.active } : layer
        )
    );
    };
    
    // Array of layer configurations - using the exact IDs from map.js
    const layers = [
        { id: "Population", label: "Population" },
        { id: "Policy Count by City", label: "Policy Count by City" },
        { id: "Count by County-Adopted Policies", label: "Policy Count by County" },
        { id: "Cultural Districts", label: "Cultural Districts" },
        { id: "Satellite", label: "Satellite" }, 
        // Removed County Boundaries and Counties Fill as requested
    ];

    // Close any open dropdown when filter menu is toggled
    const handleFilterClick = () => {
        setActiveDropdown(null); // Close any open dropdown
        closeZoomToMenu(); // Close the zoom to menu
        toggleFilterMenu(); // Toggle the filter menu
    };

    const handleZoomToClick = () => {
        setActiveDropdown(null); // Close any open dropdown in MenuButtons
        closeFilterMenu(); // Close the filter menu
        toggleZoomTo(); // Call the function from parent
    };

    return ( 
    <div className='menu_dropdowns'>
        <ul id='menu_list'>
            {/* Left side menu items */}
            <li id="menu_items">
                <button onClick={handleZoomToClick}>Search by Geography</button>
            </li>
            <li id="menu_items">
                <button 
                    onClick={handleFilterClick}
                    className={isFilterActive ? 'active-menu' : ''}
                >
                    Filter Policies {isFilterActive && <span className="filter-badge"></span>}
                </button>
            </li>
            <li id="menu_items">
                <button onClick={showTableView}>Policy Table</button>
            </li>
            <li id="menu_items">
                <ExportCsv tableData={tableData} buttonText="Export Data" />
            </li>

            {/* Right side menu items */}

            <li id="menu_items_right">
                <button onClick={showAbout}>About</button>
            </li>

            <li id="menu_items_right">
                <button onClick={showHow}>How to Use</button>
            </li>

            <li id="menu_items_right">
                <button 
                    onClick={toggleLayersMenu}
                    className={visibleLayers.length > 0 ? 'active-menu' : ''}
                >
                    More Layers
                </button>
                {activeDropdown === 'layers' && (
                    <div className={`dropdown-menu layers-dropdown${activeDropdown === 'layers' ? ' show' : ''}`}>
                        {layers.map((layer) => (
                            <button
                                key={layer.id}
                                onClick={() => toggleLayer(layer.id)}
                                className={`layer-button ${visibleLayers.includes(layer.id) ? 'active' : ''}`}
                            >
                                {layer.label}
                            </button>
                        ))}
                    </div>
                )}
            </li>
        </ul>
    </div>
    );
}

export default MenuButtons;
